import type { GroupTier, GroupTierInput } from '../hooks/use-group-tiers';

export type TierDraft = GroupTier & { isNew: boolean };

export const GROUP_TIER_DEFAULTS: Array<Partial<TierDraft>> = [
  { min_avg: 33, max_avg: 65, amount: 33, sort_order: 1 },
  { min_avg: 66, max_avg: 98, amount: 66, sort_order: 2 },
  { min_avg: 99, max_avg: null, amount: 99, sort_order: 3 },
];

export function mkRow(sort: number, seed?: Partial<TierDraft>): TierDraft {
  return {
    id: `tmp-${Date.now()}-${sort}`,
    min_avg: 0,
    max_avg: null,
    amount: 0,
    sort_order: sort,
    isNew: true,
    ...seed,
  };
}

export function toInput(d: TierDraft): GroupTierInput {
  return {
    min_avg: d.min_avg,
    max_avg: d.max_avg,
    amount: d.amount,
    sort_order: d.sort_order,
  };
}

export function adopt(server: GroupTier[]): TierDraft[] {
  return server.map((t) => ({ ...t, isNew: false }));
}

export function withPatch(
  drafts: TierDraft[],
  id: string,
  p: Partial<TierDraft>
): TierDraft[] {
  return drafts.map((d) => (d.id === id ? { ...d, ...p } : d));
}

export function seeded(): TierDraft[] {
  return GROUP_TIER_DEFAULTS.map((t, i) => mkRow(i + 1, t));
}

// Replace tier at i with `t`. If t.max_avg changed to a concrete number,
// cascade next tier's min_avg = max_avg + 1 so ranges stay contiguous.
export function cascadeUpdate(
  tiers: TierDraft[],
  i: number,
  t: TierDraft
): TierDraft[] {
  const next = [...tiers];
  next[i] = t;
  const follower = next[i + 1];
  if (t.max_avg !== null && follower)
    next[i + 1] = { ...follower, min_avg: t.max_avg + 1 };
  return next;
}

// Remove tier at i. Guard: always keep at least one tier. If removing the
// last tier, promote the new last to open-ended (max_avg = null) so the
// spectrum stays covered. Removing the first just shifts the eligibility
// threshold up to what the next tier's min_avg already is.
export function removeTier(tiers: TierDraft[], i: number): TierDraft[] {
  if (tiers.length <= 1) return tiers;
  const next = tiers.filter((_, idx) => idx !== i);
  if (i === tiers.length - 1 && next.length > 0) {
    const j = next.length - 1;
    next[j] = { ...next[j]!, max_avg: null };
  }
  return next;
}

// Insert a new tier before the (open-ended) last one. Splits the range:
// previous open tier gets a concrete max, new tier starts at max + 1.
export function insertTier(tiers: TierDraft[]): TierDraft[] {
  const last = tiers[tiers.length - 1];
  if (!last)
    return [mkRow(1, { min_avg: 1, max_avg: null, amount: 1, sort_order: 1 })];
  const cutoff = (last.min_avg ?? 0) + 10;
  const insert = mkRow(tiers.length, {
    min_avg: last.min_avg,
    max_avg: cutoff,
    amount: last.amount || 1,
    sort_order: tiers.length,
  });
  const tail = { ...last, min_avg: cutoff + 1, sort_order: tiers.length + 1 };
  return [...tiers.slice(0, -1), insert, tail];
}
